import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '@vsat/shared/lib/api';
import { useAuthStore } from '@vsat/shared/store/authStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@vsat/shared/components';
import { Button } from '@vsat/shared/components';
import { PlayCircle, Clock, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface ActiveSession {
  sessionId: string;
  examId: string;
  examTitle: string;
  subject?: string;
  remainingTime: number; // milliseconds
}

export function GlobalExamReminder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);

  // Don't show on exam session pages or if not authenticated
  const isExamPage = location.pathname.includes('/lam-bai');

  useEffect(() => {
    // CRITICAL: Don't check if not authenticated to avoid 401 → redirect → loop
    // The API will return 401, which triggers a redirect to /login, which then redirects back, causing a loop
    if (!isAuthenticated) {
      setActiveSession(null);
      setOpen(false);
      return;
    }

    // Don't check if already on exam page
    if (isExamPage) {
      setOpen(false);
      return;
    }

    // ADDITIONAL SAFETY: Ensure we have an access token before making the API call
    // This prevents the 401 error that triggers the redirect loop
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    // Only check once per session load/refresh to avoid annoyance
    // OR check every time route changes to non-exam page if we want aggressive reminder
    // Let's check on mount and then maybe periodically? For now, just on mount/auth change.

    // Check if we already ignored this session in this browser session
    const ignoredSessionId = sessionStorage.getItem('ignored_exam_session');

    const checkActiveSession = async () => {
      try {
        const session = await apiService.getActiveSessionForUser();

        if (session && session.sessionId) {
          // If we haven't ignored this specific session
          if (ignoredSessionId !== session.sessionId) {
            setActiveSession(session);
            setOpen(true);
          }
        }
      } catch (error) {
        console.error('Failed to check active session:', error);
        // Silently fail - don't show error to user for this background check
      } finally {
        setChecked(true);
      }
    };

    checkActiveSession();
  }, [isAuthenticated, isExamPage]);

  // Handle route changes - close dialog if navigating to exam
  useEffect(() => {
    if (isExamPage) {
      setOpen(false);
    }
  }, [location.pathname, isExamPage]);

  const handleResume = () => {
    if (!activeSession) return;

    setOpen(false);
    toast.info('Đang quay lại bài thi...');
    navigate(`/lam-bai/${activeSession.examId}`, {
      state: {
        resumeSession: activeSession, // Pass minimal info, ExamSession component will fetch details
        examTitle: activeSession.examTitle,
      },
    });
  };

  const handleDismiss = () => {
    setOpen(false);
    if (activeSession) {
      // Remember to ignore this session for this browser tab session
      sessionStorage.setItem('ignored_exam_session', activeSession.sessionId);
    }
  };

  if (!activeSession) return null;

  // Format remaining time
  const minutes = Math.floor(activeSession.remainingTime / 60000);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='sm:max-w-md border-orange-200 dark:border-orange-900 bg-white dark:bg-gray-900'>
        <DialogHeader>
          <div className='flex items-center gap-3 mb-2'>
            <div className='p-2 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'>
              <AlertCircle className='w-6 h-6' />
            </div>
            <DialogTitle className='text-xl text-gray-900 dark:text-white'>
              Bạn đang có bài thi làm dở!
            </DialogTitle>
          </div>
          <DialogDescription className='text-gray-600 dark:text-gray-300 text-base'>
            Bạn đang làm bài thi{' '}
            <span className='font-bold text-orange-600 dark:text-orange-400'>
              "{activeSession.examTitle}"
            </span>{' '}
            và chưa nộp bài.
          </DialogDescription>
        </DialogHeader>

        <div className='py-4'>
          <div className='flex items-center justify-between p-4 rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30'>
            <div className='flex items-center gap-2 text-gray-700 dark:text-gray-200'>
              <Clock className='w-5 h-5 text-orange-500' />
              <span>Thời gian còn lại:</span>
            </div>
            <span className='font-bold text-lg text-orange-700 dark:text-orange-400'>
              ~{minutes} phút
            </span>
          </div>
          <p className='mt-3 text-sm text-gray-500 dark:text-gray-400 italic'>
            Lưu ý: Thời gian làm bài vẫn đang trôi ngay cả khi bạn đóng trình duyệt. Vui lòng quay
            lại làm bài sớm để tránh hết giờ.
          </p>
        </div>

        <DialogFooter className='flex flex-col-reverse sm:flex-row gap-2'>
          <Button variant='outline' onClick={handleDismiss} className='w-full sm:w-auto'>
            Để sau
          </Button>
          <Button
            onClick={handleResume}
            className='w-full sm:w-auto bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0'
          >
            <PlayCircle className='w-4 h-4 mr-2' />
            Tiếp tục làm bài ngay
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
